import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Users, Contact, Briefcase, HeartPulse, PhoneCall, Loader2 } from 'lucide-react';
import { aFechaInput } from '../utils/fechas';

const OPCIONES = {
  estado: [
    { value: 'Activo', label: 'Activo' },
    { value: 'Inactivo', label: 'Inactivo' },
  ],
  rol: [
    { value: 'Administrador', label: 'Administrador' },
    { value: 'Asesor comercial', label: 'Asesor comercial' },
    { value: 'Supervisor planta', label: 'Supervisor planta' },
    { value: 'Auxiliar lavanderia', label: 'Auxiliar lavanderia' },
    { value: 'Conductor', label: 'Conductor' },
  ],
  tipoNomina: [
    { value: 'Fijo', label: 'Fijo' },
    { value: 'Temporal', label: 'Temporal' },
  ],
  parentesco: [
    { value: 'Hijo', label: 'Hijo' },
    { value: 'Hermano', label: 'Hermano' },
    { value: 'Padre', label: 'Padre' },
    { value: 'Madre', label: 'Madre' },
    { value: 'Conyuge', label: 'Cónyuge' },
    { value: 'Otros', label: 'Otros' },
  ],
  colorPlaneacion: [
    { value: 'naranja', label: 'Naranja' },
    { value: 'azul_oscuro', label: 'Azul oscuro' },
    { value: 'gris', label: 'Gris' },
    { value: 'verde_claro', label: 'Verde claro' },
    { value: 'amarillo', label: 'Amarillo' },
    { value: 'azul_claro', label: 'Azul claro' },
    { value: 'rosado', label: 'Rosado' },
    { value: 'rojo', label: 'Rojo' },
    { value: 'verde_oscuro', label: 'Verde oscuro' },
    { value: 'vino_tinto', label: 'Vino tinto' },
    { value: 'cafe', label: 'Café' },
  ],
  diaDescanso: [
    { value: 'Lunes', label: 'Lunes' },
    { value: 'Martes', label: 'Martes' },
    { value: 'Miercoles', label: 'Miércoles' },
    { value: 'Jueves', label: 'Jueves' },
    { value: 'Viernes', label: 'Viernes' },
    { value: 'Sabado', label: 'Sábado' },
    { value: 'Domingo', label: 'Domingo' },
  ],
  permiteMarcacionMovil: [
    { value: 'true', label: 'Sí' },
    { value: 'false', label: 'No' },
  ],
};

// Las secciones reemplazan la columna única de DynamicFormModal: el ancho se
// aprovecha con una rejilla y los campos se agrupan por lo que significan, que
// es más fácil de recorrer que 18 campos en fila.
const SECCIONES = [
  {
    titulo: 'Identificación',
    icon: Users,
    campos: [
      { name: 'cedula', label: 'Documento', type: 'text', required: true },
      { name: 'nombre', label: 'Nombre', type: 'text', required: true },
      { name: 'apellido', label: 'Apellido', type: 'text', required: true },
    ],
  },
  {
    titulo: 'Contacto',
    icon: Contact,
    campos: [
      { name: 'correo', label: 'Correo', type: 'email', required: true },
      { name: 'celular', label: 'Celular', type: 'tel', required: true },
      { name: 'ciudad', label: 'Ciudad', type: 'text' },
      { name: 'direccion', label: 'Dirección', type: 'text', required: true, ancho: 'md:col-span-2' },
    ],
  },
  {
    titulo: 'Información laboral',
    icon: Briefcase,
    campos: [
      { name: 'lavanderia', label: 'Centro de lavado', type: 'lavanderia', required: true },
      { name: 'rol', label: 'Rol', type: 'select', options: 'rol', required: true },
      { name: 'estado', label: 'Estado', type: 'select', options: 'estado', required: true },
      { name: 'tipoNomina', label: 'Tipo de nómina', type: 'select', options: 'tipoNomina', required: true },
      { name: 'salario', label: 'Salario mensual', type: 'number', required: true },
      { name: 'fechaIngreso', label: 'Fecha de ingreso', type: 'date' },
      { name: 'diaDescanso', label: 'Día de descanso', type: 'select', options: 'diaDescanso', required: true },
      {
        name: 'colorPlaneacion',
        label: 'Color de planeación',
        type: 'select',
        options: 'colorPlaneacion',
        required: true,
      },
      {
        name: 'permiteMarcacionMovil',
        label: 'Permite marcación móvil',
        type: 'select',
        options: 'permiteMarcacionMovil',
      },
    ],
  },
  {
    titulo: 'Salud y seguridad',
    icon: HeartPulse,
    campos: [
      { name: 'EPS', label: 'EPS', type: 'text', required: true },
    ],
  },
  {
    titulo: 'Contacto de emergencia',
    icon: PhoneCall,
    campos: [
      {
        name: 'nombre_contacto_emergencia',
        label: 'Nombre',
        type: 'text',
        required: true,
        ancho: 'md:col-span-2',
      },
      { name: 'telefono_contacto_emergencia', label: 'Teléfono', type: 'tel', required: true },
      {
        name: 'parentesco_contacto_emergencia',
        label: 'Parentesco',
        type: 'select',
        options: 'parentesco',
        required: true,
      },
    ],
  },
];

const CAMPOS_REQUERIDOS = SECCIONES.flatMap((s) =>
  s.campos.filter((c) => c.required).map((c) => c.name)
);

const valorInicial = (item) => {
  const base = {};
  SECCIONES.forEach((seccion) => {
    seccion.campos.forEach((campo) => {
      const raw = item ? item[campo.name] : '';
      if (campo.type === 'lavanderia') {
        base[campo.name] = raw ? raw.id || raw._id || '' : '';
      } else if (campo.name === 'permiteMarcacionMovil') {
        base[campo.name] =
          raw === undefined || raw === null || raw === ''
            ? ''
            : String(raw === true || raw === 'true');
      } else if (campo.name === 'fechaIngreso') {
        base[campo.name] = aFechaInput(raw);
      } else if (campo.name === 'salario') {
        base[campo.name] = raw === undefined || raw === null ? '' : String(raw).replace(/[^0-9.-]/g, '');
      } else {
        base[campo.name] = raw ?? '';
      }
    });
  });
  return base;
};

const PersonalRegistroModal = ({ isOpen, onClose, onSave, item, laundries = [], saving }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setFormData(valorInicial(item));
    setErrors({});
  }, [isOpen, item]);

  const setCampo = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validar = () => {
    const nuevos = {};
    CAMPOS_REQUERIDOS.forEach((name) => {
      const valor = formData[name];
      if (valor === undefined || valor === null || String(valor).trim() === '') {
        nuevos[name] = 'Este campo es obligatorio';
      }
    });
    setErrors(nuevos);
    return Object.keys(nuevos).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validar()) return;

    const lavanderia = laundries.find((l) => l.value === formData.lavanderia);
    const payload = {
      ...formData,
      lavanderia: lavanderia
        ? { id: lavanderia.value, nombre: lavanderia.label }
        : undefined,
      salario: Number(formData.salario),
      permiteMarcacionMovil:
        formData.permiteMarcacionMovil === ''
          ? undefined
          : formData.permiteMarcacionMovil === 'true',
    };

    onSave(payload);
  };

  const opcionesLaundries = useMemo(
    () => (Array.isArray(laundries) ? laundries : []),
    [laundries]
  );

  const hayErrores = Object.keys(errors).length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {/* DialogContent no trae alto máximo y se ancla con top-[50%]: sin
          max-h el modal crece más allá de la pantalla y el scroll interno
          no llega a activarse. Por eso se limita la altura y la caja se
          vuelve columna flexible: encabezado fijo, cuerpo con scroll y
          pie fijo. */}
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="text-xl">
            {item ? 'Editar empleado' : 'Registrar empleado'}
          </DialogTitle>
          <DialogDescription>
            Los campos marcados con * son obligatorios.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {SECCIONES.map((seccion) => {
              const Icon = seccion.icon;
              return (
                <section key={seccion.titulo}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-md bg-primary/15">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-700">{seccion.titulo}</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                    {seccion.campos.map((campo) => {
                      const id = `personal-${campo.name}`;
                      const error = errors[campo.name];
                      return (
                        <div
                          key={campo.name}
                          className={`space-y-1.5 ${campo.ancho || ''}`}
                        >
                          <Label htmlFor={id} className="text-sm text-gray-600">
                            {campo.label}
                            {campo.required && <span className="text-destructive"> *</span>}
                          </Label>

                          {campo.type === 'select' || campo.type === 'lavanderia' ? (
                            <Select
                              value={
                                formData[campo.name] !== undefined && formData[campo.name] !== null
                                  ? String(formData[campo.name])
                                  : ''
                              }
                              onValueChange={(value) => setCampo(campo.name, value)}
                            >
                              <SelectTrigger
                                id={id}
                                className={error ? 'border-destructive' : ''}
                              >
                                <SelectValue placeholder={`Seleccionar ${campo.label.toLowerCase()}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {(campo.type === 'lavanderia'
                                  ? opcionesLaundries
                                  : OPCIONES[campo.options] || []
                                ).map((opcion) => (
                                  <SelectItem
                                    key={String(opcion.value)}
                                    value={String(opcion.value)}
                                  >
                                    {opcion.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id={id}
                              type={campo.type}
                              value={formData[campo.name] ?? ''}
                              onChange={(e) => setCampo(campo.name, e.target.value)}
                              placeholder={campo.label}
                              className={error ? 'border-destructive' : ''}
                            />
                          )}

                          {error && (
                            <p className="text-xs font-medium text-destructive">{error}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-muted/30 shrink-0">
            <span className="text-xs text-muted-foreground">
              {hayErrores
                ? 'Revisa los campos marcados en rojo.'
                : 'El empleado quedará activo y visible en el listado.'}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {item ? 'Guardar cambios' : 'Registrar'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalRegistroModal;